/**
 * Pure helpers for document versions (DOCUMENT_VERSIONS_UI_PLAN.md).
 *
 * A "version" (v1, v2, ...) is the number the API assigns; a "revision" is the user-typed
 * engineering label (Rev A, 00). No imports, so `node --test` can load this file directly.
 */

/** "v3 · Rev C", "v3", or the revision label alone when the version number is unknown. */
export function versionLabel(versionNumber, revisionNumber) {
  const parts = [];
  if (versionNumber != null && versionNumber !== '') parts.push(`v${versionNumber}`);
  if (revisionNumber != null && `${revisionNumber}`.trim() !== '') parts.push(`Rev ${revisionNumber}`);
  return parts.join(' · ');
}

/** The current version embedded in a DocumentVM (list endpoints embed only that one). */
export function currentRevisionOf(document) {
  const revisions = document?.documentRevisions;
  if (!Array.isArray(revisions)) return null;
  return revisions.find((r) => r.isCurrent || r.isActive) || null;
}

/** Id of the version a DocumentVM currently serves, or null when it is not embedded. */
export function currentRevisionIdOf(document) {
  return document?.currentRevisionId || currentRevisionOf(document)?.id || null;
}

/** Outcome of POST api/documents/{id}/Revision, read from the X-Document-Version-* headers. */
export function addVersionOutcome(headers) {
  const read = (name) => {
    if (!headers) return undefined;
    if (typeof headers.get === 'function') {
      const value = headers.get(name);
      if (value != null) return value;
    }
    return headers[name] ?? headers[name.toLowerCase()];
  };
  const number = parseInt(read('X-Document-Version-Number'), 10);
  const links = parseInt(read('X-Document-Links-Transferred'), 10);
  return {
    revisionId: read('X-Document-Version-Id') || null,
    versionNumber: Number.isNaN(number) ? null : number,
    duplicate: `${read('X-Document-Version-Duplicate')}`.toLowerCase() === 'true',
    // Links copied from the previous version when the user chose to transfer them.
    linksTransferred: Number.isNaN(links) ? 0 : links,
  };
}

/** "1 link", "3 links". */
export function linkCountLabel(count) {
  const n = Number(count) || 0;
  return `${n} link${n === 1 ? '' : 's'}`;
}

/** True for the 409 the API returns when the document's rowVersion is stale. */
export function isVersionConflict(error) {
  return error?.response?.status === 409;
}

export function conflictMessage(error) {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  return 'Someone else changed this document. Reload and try again.';
}

function parseDetails(detailsJson) {
  if (!detailsJson) return {};
  try {
    const parsed = JSON.parse(detailsJson);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

// The API serialises details with PascalCase names, except the label change (from / to).
function pick(details, name) {
  return details[name] ?? details[name.charAt(0).toUpperCase() + name.slice(1)];
}

/**
 * One sentence for a DocumentVersionEventVM.
 * @param {Object} event
 * @param {Object<string, number>} versionNumbers revision id -> version number, from loaded versions
 */
export function describeHistoryEvent(event, versionNumbers = {}) {
  if (!event) return '';
  const actor = event.actorName || 'Someone';
  const details = parseDetails(event.detailsJson);
  const numberOf = (revisionId) =>
    revisionId && versionNumbers[revisionId] != null ? versionNumbers[revisionId] : null;
  const v = (n) => (n != null ? `v${n}` : 'a version');
  const own = event.versionNumber ?? numberOf(event.revisionId);

  switch (event.eventType) {
    case 'Created': {
      const file = pick(details, 'fileName');
      return `${actor} created the document${file ? ` with ${file}` : ''}`;
    }
    case 'VersionAdded': {
      const extras = [pick(details, 'revisionNumber') ? `Rev ${pick(details, 'revisionNumber')}` : null, pick(details, 'fileName')]
        .filter(Boolean)
        .join(', ');
      return `${actor} uploaded ${v(own)}${extras ? ` (${extras})` : ''}`;
    }
    case 'CurrentSet': {
      const to = numberOf(event.toRevisionId) ?? own;
      const from = numberOf(event.fromRevisionId);
      return `${actor} pinned ${v(to)} as current${from != null ? ` (was v${from})` : ''}`;
    }
    case 'CurrentCleared': {
      const to = numberOf(event.toRevisionId);
      return `${actor} reverted to the latest version${to != null ? ` (v${to})` : ''}`;
    }
    case 'LabelChanged': {
      const from = pick(details, 'from');
      const to = pick(details, 'to');
      return `${actor} changed the revision of ${v(own)}${from != null || to != null ? ` from ${from || '—'} to ${to || '—'}` : ''}`;
    }
    case 'DuplicateUploadIgnored':
      return `${actor} uploaded a file identical to ${v(own)}; no new version was created`;
    case 'Deleted':
      return own != null ? `${actor} deleted v${own}` : `${actor} deleted the document`;
    case 'Restored':
      return own != null ? `${actor} restored v${own}` : `${actor} restored the document`;
    case 'Purged':
      return `${v(own)} was permanently removed by the retention policy`;
    default:
      return `${actor}: ${event.eventType || 'change'}`;
  }
}

/**
 * Banner shown over the document viewer when the markups or the opened file are not the current
 * version. Returns null when there is nothing to say (legacy markups have no version).
 *
 * @param {Object} args
 * @param {string|null} args.markupRevisionId  ForgeAttachment.documentRevisionId
 * @param {string|null} args.viewingRevisionId the version opened, null for the current one
 * @param {Array} args.versions                DocumentVersionVM rows ({ id, versionNumber, isCurrent })
 */
export function markupVersionNotice({ markupRevisionId, viewingRevisionId, versions }) {
  if (!Array.isArray(versions) || versions.length === 0) return null;
  const current = versions.find((x) => x.isCurrent);
  if (!current) return null;
  const byId = (id) => versions.find((x) => x.id === id) || null;

  if (viewingRevisionId && viewingRevisionId !== current.id) {
    const viewing = byId(viewingRevisionId);
    return {
      kind: 'viewing-old',
      viewingRevisionId,
      viewingVersionNumber: viewing?.versionNumber ?? null,
      currentRevisionId: current.id,
      currentVersionNumber: current.versionNumber,
      markupRevisionId: markupRevisionId || null,
    };
  }

  if (markupRevisionId && markupRevisionId !== current.id) {
    const markup = byId(markupRevisionId);
    return {
      kind: 'markups-on-old',
      viewingRevisionId: null,
      markupRevisionId,
      markupVersionNumber: markup?.versionNumber ?? null,
      currentRevisionId: current.id,
      currentVersionNumber: current.versionNumber,
    };
  }
  return null;
}

/** Text for a markupVersionNotice(). */
export function noticeText(notice) {
  if (!notice) return '';
  const v = (n) => (n != null ? `v${n}` : 'an earlier version');
  if (notice.kind === 'viewing-old')
    return `You are viewing ${v(notice.viewingVersionNumber)}. The current version is v${notice.currentVersionNumber}.`;
  return `These markups were drawn on ${v(notice.markupVersionNumber)}. The current version is v${notice.currentVersionNumber}.`;
}

/**
 * Toast after adding a version from the versions dialog.
 * @param {{ duplicate: boolean, versionNumber: number|null }} outcome from addVersionOutcome()
 * @param {Object} document the DocumentVM returned by POST {id}/Revision
 * @returns {{ type: 'success'|'warning'|'info', text: string }}
 */
export function addVersionMessage(outcome, document) {
  const current = document?.currentVersionNumber;
  const added = outcome?.versionNumber ?? null;
  if (outcome?.duplicate) {
    return {
      type: 'info',
      text: `This file is identical to the current version${current ? ` (v${current})` : ''}. No new version was created.`,
    };
  }
  const links = outcome?.linksTransferred > 0 ? ` ${linkCountLabel(outcome.linksTransferred)} transferred.` : '';
  if (document?.isCurrentOverridden && added != null && added !== current) {
    return {
      type: 'warning',
      text: `Uploaded as v${added}, but v${current} stays current because it is pinned. Use Revert to latest to show it.${links}`,
    };
  }
  return {
    type: 'success',
    text: (added != null ? `Uploaded as v${added}. It is now the current version.` : 'New version uploaded.') + links,
  };
}

/**
 * SHA-256 of a file as lower-case hex, the format the API stores in ContentHash. Null when the
 * browser cannot hash (no Web Crypto outside a secure context).
 */
export async function sha256Hex(blob) {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle || !blob) return null;
  const digest = await subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * True when `file` has exactly the bytes of a stored version, given its size and SHA-256. Only a
 * file of the same size is hashed, so a different file costs nothing. No stored hash, no match.
 * @param {Blob} file
 * @param {number|string} size
 * @param {string|null} contentHash
 */
export async function hasSameContent(file, size, contentHash) {
  if (!file || !contentHash || Number(size) !== file.size) return false;
  const hash = await sha256Hex(file);
  return !!hash && hash === String(contentHash).toLowerCase();
}

/**
 * The current version when `file` has exactly its content, else null.
 * @param {Blob} file
 * @param {Array} versions DocumentVersionVMs
 */
export async function findIdenticalCurrentVersion(file, versions) {
  const current = (versions || []).find((v) => v.isCurrent);
  return current && (await hasSameContent(file, current.size, current.contentHash)) ? current : null;
}

/** "840 KB", "2.4 MB", "1.1 GB": one decimal from MB up. */
export function fileSizeLabel(bytes) {
  if (bytes == null || bytes === '') return '';
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
}

/** "4f2a1c9e…09be": enough of a SHA-256 to show two files share it. */
export function shortHash(hash) {
  const h = `${hash || ''}`.toLowerCase();
  return h.length > 16 ? `${h.slice(0, 8)}…${h.slice(-4)}` : h;
}

/** The number the next upload is expected to get: one above the highest version seen. */
export function expectedNextVersion(versionNumbers) {
  const numbers = Object.values(versionNumbers || {}).filter((n) => Number.isFinite(n));
  return numbers.length ? Math.max(...numbers) + 1 : null;
}
